import { useEffect, useMemo, useState } from 'react';

/**
 * Scenario imagery — the splash plate, the side crests, and the seat patches.
 *
 * All of it is per-scenario art, uploaded as image files, so none of it is
 * hardcoded here. A slot resolves against the scenario first and the shared
 * `_default` set second:
 *
 *     <base>art/<scenario-slug>/<slot>.<ext>
 *     <base>art/_default/<slot>.<ext>
 *
 * and when neither exists it renders the design's own empty-slot placeholder —
 * the `image-slot::part(empty)` state the mockups in `docs/frontend/page-files/`
 * show before art lands. That is a resting state, not an error, and it occupies
 * exactly the same box, so nothing in the composition moves when the art
 * arrives.
 *
 * The scenario name is the key because that is all the wire carries today
 * (`GameSummary.scenario`); there is no imagery field on the protocol yet, and
 * the entry screen runs before any of it is known, which is why entry only ever
 * gets `_default`. Both are noted in `docs/todo.md`.
 */

const ART_BASE = `${import.meta.env.BASE_URL}art/`;

/**
 * The credit line the entry screen draws under the splash plate.
 *
 * Public-affairs imagery is public domain, not anonymous — the mockup carries
 * the credit and so does this. It renders only once the plate has actually
 * loaded, so a missing file is a blank corner rather than a credit for a
 * photograph nobody can see. Change the plate in
 * `packages/client/public/art/_default/` and change this with it.
 */
export const SPLASH_CREDIT = 'Image: U.S. Space Force · J. Masie';

/**
 * Every candidate that gets tried is one request, and a miss is one 404 in the
 * console — so the list stays short and the results are remembered. Crests and
 * patches want transparency (PNG, or WebP for a large one); the splash is a
 * photograph, so it leads with JPEG.
 */
/**
 * `svg` leads because the demo's cloth is drawn rather than photographed
 * (`packages/client/scripts/build-crests.mjs`): vector insignia stays crisp at
 * the 40px the identity row shows a crest at and the 96px a seat card shows a
 * patch at, which is the whole range these live in. Raster stays behind it for
 * art that really is a photograph of a patch.
 */
const PATCH_EXTENSIONS = ['svg', 'png', 'webp'] as const;
const PHOTO_EXTENSIONS = ['jpg', 'webp', 'png'] as const;

/**
 * url → does it exist. Seat cards remount on every tab switch, and without this
 * each switch would re-request sixteen files that were already known absent.
 */
const probed = new Map<string, boolean>();

/**
 * Slot ids. Stable strings, because they are also the filenames art is uploaded
 * under.
 *
 * `seatPatch` returns a *list*, most specific first. A cell is a unit — BLUFOR's
 * missile warning seat is one squadron and REDFOR's is another, and they wear
 * different cloth — so the side-specific file is what a scenario should ship.
 * The bare `seat-<cell>` name stays as the fallback for art that genuinely is
 * shared, and so that patches produced before this still resolve.
 */
export const SLOT = {
  splash: 'splash',
  sideCrest: (role: string): string => `crest-${role.toLowerCase()}`,
  seatPatch: (cell: string, side?: string | null): readonly string[] =>
    side
      ? [`seat-${side.toLowerCase()}-${cell.toLowerCase()}`, `seat-${cell.toLowerCase()}`]
      : [`seat-${cell.toLowerCase()}`],
} as const;

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/** Every url `ImageSlot` would try for this slot, root-major: scenario before `_default`. */
function candidateUrls(
  scenario: string | null | undefined,
  names: readonly string[],
  photo: boolean | undefined,
): readonly string[] {
  const slug = scenario ? slugify(scenario) : null;
  const roots = slug ? [slug, '_default'] : ['_default'];
  const extensions = photo ? PHOTO_EXTENSIONS : PATCH_EXTENSIONS;
  return roots.flatMap((root) =>
    names.flatMap((name) => extensions.map((ext) => `${ART_BASE}${root}/${name}.${ext}`)),
  );
}

/**
 * Warms `probed` for slots the caller knows are coming, before anything asks
 * `ImageSlot` to draw them.
 *
 * Seat select's card grid lands eight slots at once (seat-select.md), and each
 * one `ImageSlot` has never seen costs it a walk through every candidate —
 * scenario png, scenario webp, `_default` png, `_default` webp — one real
 * round trip at a time, waiting on each `onError` before the next. Landing
 * together, that is up to eight slots doing that walk *concurrently but each
 * serially*, which is exactly the shape of a visible multi-second stall.
 *
 * This fires every candidate for every slot at once instead of one at a time,
 * as soon as the caller knows the scenario and the roster — which is earlier
 * than the slots themselves mount, so the animated beat between (the entry
 * handoff, seat-select's own entrance) is what pays for it instead of the
 * user staring at empty boxes. Every url still costs one request; this only
 * moves them earlier and runs them in parallel rather than one at a time.
 * `ImageSlot`'s own walk then finds `probed` already answered and skips
 * straight to the one url that resolved, or straight to the placeholder.
 */
export function preloadSlots(
  scenario: string | null | undefined,
  slots: readonly (string | readonly string[])[],
  photo?: boolean,
): void {
  for (const slot of slots) {
    const names = typeof slot === 'string' ? [slot] : slot;
    for (const url of candidateUrls(scenario, names, photo)) {
      if (probed.has(url)) continue;
      // Never awaited and never mounted — a plain warm-up. `probed` is the
      // only thing this writes to; `ImageSlot` reads it back independently.
      const img = new Image();
      img.onload = () => probed.set(url, true);
      img.onerror = () => probed.set(url, false);
      img.src = url;
    }
  }
}

export interface ImageSlotProps {
  /**
   * One of `SLOT` — also the filename the art is uploaded under. A list means
   * "most specific first": every name is tried under the scenario before any of
   * them is tried under `_default`, so a scenario's own generic patch beats the
   * shared set's specific one.
   */
  readonly slot: string | readonly string[];
  /** Scenario name from `GameSummary.scenario`; null before it is known. */
  readonly scenario?: string | null;
  /** The empty-state label. Omit on small boxes — see `compact`. */
  readonly placeholder?: string;
  /** Boxes too small to letter (the 41 px header crest): empty renders blank. */
  readonly compact?: boolean;
  /** Shown instead of the lettered placeholder when there is no art at all. */
  readonly emptyFill?: string;
  /** Photographic plate rather than a patch — changes which formats are tried first. */
  readonly photo?: boolean;
  readonly alt?: string;
  /**
   * Whether art was found, once the walk has finished. The splash uses it to
   * decide whether there is a photograph to credit; nothing else needs it.
   */
  readonly onResolved?: (found: boolean) => void;
}

/**
 * One art slot. Walks scenario → default → placeholder, and every miss is an
 * `onError` rather than a probe, so a slot that has art costs exactly one
 * request and a slot that has none costs no layout shift.
 */
export function ImageSlot(props: ImageSlotProps): JSX.Element {
  const { slot, scenario, compact, placeholder, emptyFill, photo, alt, onResolved } = props;

  // A stable key for the slot list, so an inline `SLOT.seatPatch(...)` array
  // does not look like a new value on every render and restart the walk.
  const names = typeof slot === 'string' ? slot : slot.join('|');

  const candidates = useMemo(() => {
    // Root-major: everything the scenario might have, then the shared set.
    const all = candidateUrls(scenario, names.split('|'), photo);
    // A url already known to exist short-circuits the walk; ones known absent
    // drop out of it, so a remount costs nothing it has already paid for —
    // including what `preloadSlots` paid for before this ever mounted.
    const known = all.find((url) => probed.get(url) === true);
    return known !== undefined ? [known] : all.filter((url) => probed.get(url) !== false);
  }, [scenario, names, photo]);

  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setAttempt(0);
    setLoaded(false);
  }, [candidates]);

  const src = candidates[attempt];

  // The walk is over either when something loaded or when the candidates ran
  // out. Reported from an effect rather than from the load handler, because a
  // parent that reacts by rendering must not be told to update mid-render.
  const settled = src === undefined || loaded;
  useEffect(() => {
    if (settled) onResolved?.(loaded);
  }, [settled, loaded, onResolved]);

  if (src !== undefined) {
    return (
      <img
        key={src}
        src={src}
        alt={alt ?? ''}
        onLoad={() => {
          probed.set(src, true);
          setLoaded(true);
        }}
        onError={() => {
          probed.set(src, false);
          setAttempt((n) => n + 1);
        }}
        // A photograph is a plate and may be cropped to fill its box. A crest or
        // a patch may not: it is a piece of insignia with a shape, and `cover`
        // guillotines a tall one at the top and bottom rather than sizing it to
        // fit. `contain` lets the box be the limit and the art keep its outline.
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: photo ? 'cover' : 'contain',
        }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: emptyFill,
        fontFamily: 'var(--font-data)',
        fontSize: 10,
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: 'var(--text-disabled)',
        textAlign: 'center',
        padding: 4,
        overflow: 'hidden',
      }}
    >
      {compact ? '' : (placeholder ?? '')}
    </div>
  );
}
